import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Add20Regular,
  Search20Regular,
  Building24Regular,
  Edit20Regular,
  ArrowLeft24Regular,
} from "@fluentui/react-icons";
import { PageHeader } from "../../components/PageHeader";
import {
  ApiError,
  getPropertyById,
  getProperties,
  type Property as ApiProperty,
} from "../../imoveis/imoveisApi";
import {
  getRentalContracts,
  type RentalContract,
} from "../../arrendamentos/arrendamentosApi";
import {
  getPropertyProfitability,
  type PropertyProfitability,
} from "../../relatorios/relatoriosApi";
import { rentalStatusBadgeClass } from "../../arrendamentos/arrendamentoFormat";
import { formatDateShort } from "../Obras/obraFormat";

interface ImovelView {
  id: number;
  nome: string;
  morada?: string | null;
  tipologia?: string | null;
  area?: number | null;
  renda?: number | null;
  imi?: number | null;
}

const formatOptional = (value?: string | number | null): string => {
  if (value === undefined || value === null) return "—";
  if (typeof value === "string") return value.trim() ? value : "—";
  return String(value);
};

const formatCurrency = (value?: number | null): string => {
  if (value === undefined || value === null) return "—";
  return value.toLocaleString("pt-PT", { style: "currency", currency: "EUR" });
};

const parseErrorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status === 401) {
      return "Sessão expirada. Por favor, autentique-se novamente.";
    }
    if (error.status === 403) {
      return "Sem permissões para consultar imóveis.";
    }
  }

  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Não foi possível carregar os imóveis.";
};

const mapPropertyToView = (property: ApiProperty): ImovelView => {
  return {
    id: property.id,
    nome: property.name,
    morada: property.address ?? null,
    tipologia: property.propertyType ?? null,
    area: property.area ?? null,
    renda: property.rentValue ?? null,
    imi: property.annualImi ?? null,
  };
};

const getInitials = (nome: string) => {
  return nome
    .split(" ")
    .map((word) => word[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

export function ImoveisView() {
  const navigate = useNavigate();
  const [imoveis, setImoveis] = useState<ImovelView[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isMobileDetailOpen, setIsMobileDetailOpen] = useState(false);
  // Histórico de Arrendamento [5.1.2] — efeito/estado próprios, sem tocar na
  // seleção/fetch do imóvel.
  const [contracts, setContracts] = useState<RentalContract[]>([]);
  const [contractsLoading, setContractsLoading] = useState(false);
  // Rentabilidade por imóvel [5.5.2] — receita recebida − IMI anual, por ano.
  const [profitabilityYear, setProfitabilityYear] = useState(
    new Date().getFullYear(),
  );
  const [profitability, setProfitability] =
    useState<PropertyProfitability | null>(null);
  const [profitabilityLoading, setProfitabilityLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const loadProperties = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const response = await getProperties();
        if (!isMounted) return;

        const mapped = response.map(mapPropertyToView);
        setImoveis(mapped);
        setSelectedPropertyId((current) => {
          if (current && mapped.some((item) => item.id === current)) {
            return current;
          }
          return mapped[0]?.id ?? null;
        });
      } catch (error) {
        if (!isMounted) return;
        setImoveis([]);
        setSelectedPropertyId(null);
        setLoadError(parseErrorMessage(error));
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadProperties();

    return () => {
      isMounted = false;
    };
  }, []);

  const filteredImoveis = useMemo(
    () =>
      imoveis.filter((imovel) => {
        const matchesSearch =
          imovel.nome
            .toLowerCase()
            .includes(searchQuery.toLowerCase()) ||
          (imovel.morada ?? "")
            .toLowerCase()
            .includes(searchQuery.toLowerCase());
        return matchesSearch;
      }),
    [imoveis, searchQuery],
  );

  useEffect(() => {
    if (isLoading) return;

    if (filteredImoveis.length === 0) {
      setSelectedPropertyId(null);
      return;
    }

    const hasSelection = filteredImoveis.some(
      (entry) => entry.id === selectedPropertyId,
    );
    if (!hasSelection) {
      setSelectedPropertyId(filteredImoveis[0].id);
    }
  }, [filteredImoveis, isLoading, selectedPropertyId]);

  useEffect(() => {
    if (!selectedPropertyId) return;

    let isMounted = true;

    const loadSelectedProperty = async () => {
      try {
        const response = await getPropertyById(selectedPropertyId);
        if (!isMounted) return;

        const mapped = mapPropertyToView(response);
        setImoveis((current) =>
          current.map((imovel) =>
            imovel.id === selectedPropertyId ? mapped : imovel,
          ),
        );
      } catch {
        // Keep list data if detail fetch fails.
      }
    };

    loadSelectedProperty();

    return () => {
      isMounted = false;
    };
  }, [selectedPropertyId]);

  useEffect(() => {
    if (!selectedPropertyId) {
      setContracts([]);
      return;
    }

    let isMounted = true;
    setContractsLoading(true);

    getRentalContracts({ propertyId: selectedPropertyId })
      .then((data) => {
        if (!isMounted) return;
        const sorted = [...data].sort(
          (a, b) =>
            new Date(b.startDate).getTime() - new Date(a.startDate).getTime(),
        );
        setContracts(sorted);
      })
      .catch(() => {
        if (isMounted) setContracts([]);
      })
      .finally(() => {
        if (isMounted) setContractsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedPropertyId]);

  useEffect(() => {
    if (!selectedPropertyId) {
      setProfitability(null);
      return;
    }

    let isMounted = true;
    setProfitabilityLoading(true);

    getPropertyProfitability(selectedPropertyId, profitabilityYear)
      .then((data) => {
        if (isMounted) setProfitability(data);
      })
      .catch(() => {
        if (isMounted) setProfitability(null);
      })
      .finally(() => {
        if (isMounted) setProfitabilityLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [selectedPropertyId, profitabilityYear]);

  const selectedImovel = filteredImoveis.find(
    (i) => i.id === selectedPropertyId,
  );

  const handlePropertySelect = (propertyId: number) => {
    setSelectedPropertyId(propertyId);
    setIsMobileDetailOpen(true);
  };

  const handleMobileBack = () => {
    setIsMobileDetailOpen(false);
  };

  const actions = (
    <>
      <button
        className="px-4 py-2 bg-primary hover:bg-primary/90 text-primary-foreground rounded transition-colors flex items-center gap-2 min-h-[44px]"
        style={{
          borderRadius: "var(--radius-button)",
          height: "var(--button-height)",
        }}
        onClick={() => navigate("/imoveis/novo")}
      >
        <Add20Regular className="w-5 h-5" />
        Novo Imóvel
      </button>
    </>
  );

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Imóveis"
        subtitle="Lista de imóveis e dados de arrendamento"
        actions={actions}
      />

      <div className="flex-1 flex overflow-hidden">
        <div
          className={`${
            isMobileDetailOpen ? "hidden md:flex" : "flex"
          } flex-col w-full md:w-80 lg:w-96 border-r border-border bg-muted/30`}
        >
          <div className="p-4 border-b border-border bg-card">
            <div className="relative">
              <Search20Regular className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-text" />
              <input
                type="text"
                placeholder="Procurar imóveis..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-border rounded bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20"
                style={{ borderRadius: "var(--radius-button)" }}
              />
            </div>

            <div className="text-sm text-[var(--muted-foreground)] mt-3">
              {isLoading
                ? "A carregar imóveis..."
                : `${filteredImoveis.length} imóve${
                    filteredImoveis.length !== 1 ? "is" : "l"
                  } encontrado${
                    filteredImoveis.length !== 1 ? "s" : ""
                  }`}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Building24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--muted-foreground)] text-center">
                  A carregar imóveis...
                </p>
              </div>
            )}

            {!isLoading && loadError && (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Building24Regular className="text-[var(--muted-foreground)] mb-4" />
                <p className="text-[var(--destructive)] text-center">
                  {loadError}
                </p>
              </div>
            )}

            {!isLoading &&
              !loadError &&
              filteredImoveis.map((imovel) => (
                <button
                  key={imovel.id}
                  onClick={() => handlePropertySelect(imovel.id)}
                  className={`w-full text-left p-4 border-b border-border transition-colors relative ${
                    selectedPropertyId === imovel.id ? "bg-accent/50" : ""
                  }`}
                  style={{
                    backgroundColor:
                      selectedPropertyId === imovel.id
                        ? "var(--accent)"
                        : "var(--card)",
                  }}
                  onMouseEnter={(e) => {
                    if (selectedPropertyId !== imovel.id) {
                      e.currentTarget.style.backgroundColor = "var(--accent)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedPropertyId !== imovel.id) {
                      e.currentTarget.style.backgroundColor = "var(--card)";
                    }
                  }}
                >
                  {selectedPropertyId === imovel.id && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary" />
                  )}

                  <div className="flex items-start gap-3 pl-2">
                    <div
                      className="w-10 h-10 bg-primary/10 flex items-center justify-center flex-shrink-0"
                      style={{
                        fontSize: "var(--text-caption)",
                        fontWeight: "var(--font-weight-semi-bold)",
                        color: "var(--primary)",
                        borderRadius: "var(--radius)",
                      }}
                    >
                      {getInitials(imovel.nome)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <p
                        className="truncate"
                        style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                      >
                        {imovel.nome}
                      </p>

                      <p
                        className="text-secondary-text mt-0.5"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {formatOptional(imovel.tipologia)}
                      </p>

                      <p
                        className="text-secondary-text mt-1"
                        style={{ fontSize: "var(--text-caption)" }}
                      >
                        {formatOptional(imovel.morada)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}

            {!isLoading && !loadError && filteredImoveis.length === 0 && (
              <div className="p-8 text-center text-secondary-text">
                <p>Nenhum imóvel encontrado</p>
              </div>
            )}
          </div>
        </div>

        <div
          className={`${
            isMobileDetailOpen ? "flex" : "hidden md:flex"
          } flex-1 flex-col overflow-y-auto bg-background`}
        >
          {!selectedImovel ? (
            <div className="flex-1 flex items-center justify-center p-8">
              <div className="text-center max-w-md">
                <Building24Regular className="w-16 h-16 mx-auto text-secondary-text mb-4" />
                <p
                  className="text-secondary-text"
                  style={{ fontSize: "var(--text-base)" }}
                >
                  Selecione um imóvel para ver os detalhes
                </p>
              </div>
            </div>
          ) : (
            <div className="p-4 md:p-6 space-y-6">
              <button
                onClick={handleMobileBack}
                className="md:hidden flex items-center gap-2 text-secondary-text hover:text-foreground transition-colors mb-4"
              >
                <ArrowLeft24Regular className="w-6 h-6" />
                <span>Voltar</span>
              </button>

              <div
                className="bg-card border border-border p-6"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div
                      className="w-16 h-16 bg-primary/10 flex items-center justify-center flex-shrink-0"
                      style={{
                        fontSize: "var(--text-large)",
                        fontWeight: "var(--font-weight-semi-bold)",
                        color: "var(--primary)",
                        borderRadius: "var(--radius)",
                      }}
                    >
                      {getInitials(selectedImovel.nome)}
                    </div>

                    <div>
                      <h3
                        className="text-foreground"
                        style={{
                          fontSize: "var(--text-large)",
                          fontWeight: "var(--font-weight-semi-bold)",
                          lineHeight: "1.3",
                        }}
                      >
                        {selectedImovel.nome}
                      </h3>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      className="px-4 py-2 bg-secondary hover:bg-accent border border-border rounded transition-colors flex items-center gap-2 min-h-[44px]"
                      style={{ borderRadius: "var(--radius-button)" }}
                      onClick={() =>
                        navigate(`/imoveis/editar/${selectedImovel.id}`)
                      }
                    >
                      <Edit20Regular className="w-5 h-5" />
                      <span>Editar Imóvel</span>
                    </button>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Dados do Imóvel
                </h4>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Morada
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedImovel.morada)}</p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Tipologia
                    </p>
                    <p className="mt-0.5">{formatOptional(selectedImovel.tipologia)}</p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Área (m²)
                    </p>
                    <p className="mt-0.5">
                      {selectedImovel.area !== null && selectedImovel.area !== undefined
                        ? `${selectedImovel.area} m²`
                        : "—"}
                    </p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Valor de Renda
                    </p>
                    <p className="mt-0.5">{formatCurrency(selectedImovel.renda)}</p>
                  </div>

                  <div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      IMI Anual
                    </p>
                    <p className="mt-0.5">{formatCurrency(selectedImovel.imi)}</p>
                  </div>
                </div>
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <h4
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-section-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                  }}
                >
                  Histórico de Arrendamento
                </h4>

                {contractsLoading ? (
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    A carregar…
                  </p>
                ) : contracts.length === 0 ? (
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Sem histórico de arrendamento.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {contracts.map((contract) => (
                      <div
                        key={contract.id}
                        className="border border-border p-4"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        <div className="flex items-start justify-between gap-3 flex-wrap">
                          <div className="min-w-0">
                            <p
                              className="truncate"
                              style={{
                                fontWeight: "var(--font-weight-semi-bold)",
                              }}
                            >
                              {contract.tenantName}
                            </p>
                            <p
                              className="text-secondary-text mt-0.5"
                              style={{ fontSize: "var(--text-caption)" }}
                            >
                              {formatDateShort(contract.startDate)} —{" "}
                              {contract.endDate
                                ? formatDateShort(contract.endDate)
                                : "sem termo"}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span
                              className={`inline-flex items-center px-2 py-1 text-xs ${rentalStatusBadgeClass(
                                contract.statusCode,
                              )}`}
                              style={{ borderRadius: "var(--radius)" }}
                            >
                              {contract.statusName}
                            </span>
                            {contract.statusCode === "ativo" && (
                              <span
                                className="inline-flex items-center px-2 py-0.5 text-xs bg-primary/10 text-primary"
                                style={{ borderRadius: "var(--radius)" }}
                              >
                                Contrato atual
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-between gap-3 mt-2">
                          <span
                            className="text-secondary-text"
                            style={{ fontSize: "var(--text-caption)" }}
                          >
                            Renda mensal
                          </span>
                          <span className="whitespace-nowrap">
                            {formatCurrency(contract.monthlyRent)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div
                className="bg-card border border-border p-6 space-y-4"
                style={{ borderRadius: "var(--radius-card)" }}
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <h4
                    className="text-foreground"
                    style={{
                      fontSize: "var(--text-section-title)",
                      fontWeight: "var(--font-weight-semi-bold)",
                    }}
                  >
                    Rentabilidade
                  </h4>
                  <select
                    value={profitabilityYear}
                    onChange={(event) =>
                      setProfitabilityYear(Number(event.target.value))
                    }
                    className="px-3 py-2 border border-border bg-background text-foreground text-sm"
                    style={{ borderRadius: "var(--radius)" }}
                  >
                    {[0, 1, 2, 3, 4].map((offset) => {
                      const y = new Date().getFullYear() - 3 + offset;
                      return (
                        <option key={y} value={y}>
                          {y}
                        </option>
                      );
                    })}
                  </select>
                </div>

                {profitabilityLoading ? (
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    A carregar…
                  </p>
                ) : !profitability ? (
                  <p
                    className="text-secondary-text"
                    style={{ fontSize: "var(--text-caption)" }}
                  >
                    Sem dados de rentabilidade.
                  </p>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div
                        className="border border-border p-3"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        <p
                          className="text-secondary-text"
                          style={{ fontSize: "var(--text-caption)" }}
                        >
                          Receita recebida
                        </p>
                        <p
                          className="mt-0.5"
                          style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                        >
                          {formatCurrency(profitability.rentReceived)}
                        </p>
                      </div>
                      <div
                        className="border border-border p-3"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        <p
                          className="text-secondary-text"
                          style={{ fontSize: "var(--text-caption)" }}
                        >
                          IMI anual
                        </p>
                        <p
                          className="mt-0.5"
                          style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                        >
                          {formatCurrency(profitability.annualImi)}
                        </p>
                      </div>
                      <div
                        className="border border-border p-3"
                        style={{ borderRadius: "var(--radius)" }}
                      >
                        <p
                          className="text-secondary-text"
                          style={{ fontSize: "var(--text-caption)" }}
                        >
                          Rentabilidade líquida
                        </p>
                        <p
                          className={`mt-0.5 ${
                            profitability.netProfitability >= 0
                              ? "text-green-700"
                              : "text-destructive"
                          }`}
                          style={{ fontWeight: "var(--font-weight-semi-bold)" }}
                        >
                          {formatCurrency(profitability.netProfitability)}
                        </p>
                      </div>
                    </div>
                    <p
                      className="text-secondary-text"
                      style={{ fontSize: "var(--text-caption)" }}
                    >
                      Receita esperada {formatCurrency(profitability.rentExpected)}{" "}
                      · por receber{" "}
                      {formatCurrency(
                        profitability.rentExpected - profitability.rentReceived,
                      )}{" "}
                      · líquida = recebida − IMI anual
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
